import { usePagination } from 'Hooks/usePagination'
import { useGetDomains } from '../api/getDomains'
import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetMigrationMonitorMode } from '../api/getMigrationMonitorMode'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { useMigrationDisplay } from 'Hooks/useMigrationDisplay'
import { TableWithPaginationProps } from '@clickcease/cc-client-components-lib'
import { DomainManagementRow } from '../components/DomainManagmentRow/DomainManagementRow'
import { useGetTags } from '../api/getBzMonitorMode'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { TableHeaderCounter } from 'Features/domainsManagement/components/TableHeaderCounter/TableHeaderCounter'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { PLAN_TYPES } from 'Types/PlansTypes'
import { TableHeaderTitle } from 'Features/domainsManagement/routes/DomainManagement/DomainManagement.styled'

export const useDomainsPagination = () => {
  const { t } = useTranslation('domainManagement')
  const { isMigratedAccount } = useMigrationDisplay()
  const [pageCount, setPageCount] = useState<number>(0)
  const [limit, setLimit] = useState<number>(50)
  const [extraOptionsModalId, setModalExtraOptionsId] = useState<number>(0)
  const {
    currentPage,
    search,
    onSearchInputChange,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPreviousPage,
    debouncedSearch,
  } = usePagination({ pageCount })
  const { data: migrationDomainsList } = useGetMigrationMonitorMode(isMigratedAccount)
  const { data: packagesData } = useGetPackagingLimits()
  const { data: domainsData, isFetching } = useGetDomains({
    page: currentPage,
    search: debouncedSearch,
    limit,
  })
  const [numOfPaidMarketingDomains, setNumOfPaidMarketingDomains] = useState(0)
  const [numOfBotzappingDomains, setNumOfBotzappingDomains] = useState(0)
  const { data: tagsData } = useGetTags()
  const { domains: serverDomains, metadata } = domainsData?.data || {}
  const { data: planDetails } = useGetPlansDetails()

  const domains = useMemo(() => {
    if (serverDomains && tagsData) {
      return serverDomains.map((domain) => {
        const tags = tagsData.find((tag) => tag.searchId === domain.tagId)
        const isBzMonitor =
          tags && tags.monitoringMode.isMonitoring ? tags.monitoringMode.isMonitoring : false
        return { ...domain, isBzMonitor }
      })
    } else {
      return []
    }
  }, [serverDomains, tagsData])

  useEffect(() => {
    setNumOfPaidMarketingDomains(metadata?.totalPmDomains || 0)
    setNumOfBotzappingDomains(metadata?.totalBmDomains || 0)
  }, [metadata])

  if (metadata?.pageCount && metadata?.pageCount !== pageCount) {
    setPageCount(metadata?.pageCount)
  }

  // to propagate open GetTag modal after navigation from other pages - we check if modalToShow is GetTag and force open it through the first table row
  const { modalToShow } = useAppStore()
  const forceOpenGetTagModal = modalToShow === GLOBAL_MODALS.GET_TAG

  const columnsNames = (() => {
    const names: Array<string | React.ReactNode> = [
      <TableHeaderTitle key={'domain'}>{t('domain')}</TableHeaderTitle>,
      <TableHeaderTitle key={'tag'}>{t('tag_management')}</TableHeaderTitle>,
    ]
    if (migrationDomainsList?.length) names.push(t('clickcease'))
    if (packagesData?.paidMarketingAllowed)
      names.push(
        <TableHeaderCounter
          title={t('paid_marketing')}
          numOfDomains={numOfPaidMarketingDomains}
          maxNumOfDomains={packagesData?.paidMarketingDomainsLimit || 0}
        />
      )
    if (packagesData?.botZappingAllowed)
      names.push(
        <TableHeaderCounter
          title={t('bot_zapping')}
          numOfDomains={numOfBotzappingDomains}
          maxNumOfDomains={packagesData?.botZappingDomainsLimit || 0}
        />
      )
    return names
  })()

  const renderTableBody = () => {
    return domains?.map((domain, indx) => (
      <DomainManagementRow
        key={domain.id}
        domain={domain}
        shouldShowExtraOptionsModal={domain.id === extraOptionsModalId}
        setExtraModalOn={(close) => setModalExtraOptionsId(close == 0 ? 0 : domain.id)}
        forceOpenGetTagModal={forceOpenGetTagModal && indx === 0}
      />
    ))
  }

  const shouldShowUpgradeButton = useMemo(() => {
    //if paid marketing or bot zapping has reached domains limit or is approaching it
    const reachedOrApproaching =
      (packagesData?.paidMarketingDomainsLimit || 0) - 1 === numOfPaidMarketingDomains ||
      packagesData?.paidMarketingDomainsLimit === numOfPaidMarketingDomains ||
      packagesData?.botZappingDomainsLimit === numOfBotzappingDomains ||
      (packagesData?.botZappingDomainsLimit || 0) - 1 === numOfBotzappingDomains

    const pmHasRegularPlan =
      planDetails?.plans[0]?.type === PLAN_TYPES.CORE ||
      planDetails?.plans[0]?.type === PLAN_TYPES.ADVANCED
    const bmHasRegularPlan =
      planDetails?.plans[1]?.type === PLAN_TYPES.CORE ||
      planDetails?.plans[1]?.type === PLAN_TYPES.ADVANCED
    return reachedOrApproaching && (pmHasRegularPlan || bmHasRegularPlan)
  }, [packagesData, numOfPaidMarketingDomains, numOfBotzappingDomains, planDetails])

  const tableProps: TableWithPaginationProps = {
    currentPage,
    pageCount,
    columnsNames,
    totalItems: metadata?.totalCount ? metadata?.totalCount : undefined,
    body: renderTableBody(),
    onFirstPage,
    onLastPage,
    onNextPage,
    onPreviousPage,
  }

  return {
    currentPage,
    isFetching,
    searchText: search,
    setLimit,
    onSearchInputChange,
    domains,
    metadata,
    tableProps,
    shouldShowUpgradeButton,
  }
}
